/**
 * Feature Flags Page
 *
 * Manage feature flags with toggle, create, and edit functionality.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { cn, Button, Input } from '@respectly/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@respectly/ui';
import {
  Flag,
  Plus,
  Search,
  RefreshCw,
  ArrowLeft,
  X,
} from 'lucide-react';
import type { FeatureFlag, FeatureFlagScope } from '@respectly/shared-types';
import {
  useFeatureFlagsStore,
  useFeatureFlagFiltersCount,
  useEnabledFlagsCount,
} from '../../stores/useFeatureFlagsStore';
import { usePlansStore } from '../../stores/usePlansStore';
import { useAccountsStore } from '../../stores/useAccountsStore';
import {
  FeatureFlagList,
  FeatureFlagToggleSkeleton,
  FeatureFlagModal,
  getScopeOptions,
} from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Component
// ============================================

export function FeatureFlags() {
  const {
    flags,
    isLoadingList,
    isToggling,
    filters,
    setFilter,
    clearFilters,
    loadFlags,
    toggleFlag,
    updateFlag,
    createFlag,
    deleteFlag,
    isUpdating,
    isCreating,
  } = useFeatureFlagsStore();

  // Real plan + tenant lists for the modal's scope-target pickers.
  const plans = usePlansStore((s) => s.plans);
  const loadPlans = usePlansStore((s) => s.loadPlans);
  const accounts = useAccountsStore((s) => s.accounts);
  const loadAccounts = useAccountsStore((s) => s.loadAccounts);

  const activeFiltersCount = useFeatureFlagFiltersCount();
  const enabledCount = useEnabledFlagsCount();
  const scopeOptions = getScopeOptions();

  // Local state
  const [searchValue, setSearchValue] = useState(filters.search ?? '');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);

  // Load flags + plan/tenant pickers on mount
  useEffect(() => {
    loadFlags();
    loadPlans();
    loadAccounts();
  }, [loadFlags, loadPlans, loadAccounts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        setFilter('search', searchValue || undefined);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, filters.search, setFilter]);

  // Handle toggle
  const handleToggle = async (flag: FeatureFlag, enabled: boolean) => {
    await toggleFlag(flag.id, enabled);
  };

  // Handle edit
  const handleEdit = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setModalOpen(true);
  };

  // Handle create
  const handleCreate = () => {
    setSelectedFlag(null);
    setModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (flag: FeatureFlag) => {
    if (confirm(`Are you sure you want to delete "${flag.name}"?`)) {
      await deleteFlag(flag.id);
    }
  };

  // Handle form submit
  const handleSubmit = async (data: unknown) => {
    if (selectedFlag) {
      return await updateFlag(selectedFlag.id, data as Record<string, unknown>);
    }
    const id = await createFlag(data as Parameters<typeof createFlag>[0]);
    return id !== null;
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/settings"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 flex items-center justify-center">
              <Flag className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Feature Flags
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                {enabledCount} of {flags.length} flags enabled
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadFlags()}
              disabled={isLoadingList}
            >
              <RefreshCw
                className={cn('w-4 h-4 mr-2', isLoadingList && 'animate-spin')}
              />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Create Flag
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mt-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search flags..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={filters.scope ?? 'all'}
            onValueChange={(value) =>
              setFilter('scope', value === 'all' ? undefined : (value as FeatureFlagScope))
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Scopes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Scopes</SelectItem>
              {scopeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.enabled === undefined ? 'all' : String(filters.enabled)}
            onValueChange={(value) =>
              setFilter('enabled', value === 'all' ? undefined : value === 'true')
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Enabled</SelectItem>
              <SelectItem value="false">Disabled</SelectItem>
            </SelectContent>
          </Select>

          {activeFiltersCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-500"
            >
              <X className="w-4 h-4 mr-1" />
              Clear ({activeFiltersCount})
            </Button>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoadingList ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <FeatureFlagToggleSkeleton key={i} />
            ))}
          </div>
        ) : (
          <motion.div variants={itemVariants}>
            <FeatureFlagList
              flags={flags}
              togglingId={isToggling}
              onToggle={handleToggle}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <FeatureFlagModal
        flag={selectedFlag}
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={handleSubmit}
        isLoading={isUpdating || isCreating}
        plans={plans.map((p) => ({ id: p.id, name: p.name }))}
        tenants={accounts.map((a) => ({ id: a.id, name: a.name }))}
      />
    </motion.div>
  );
}
